import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import SeoHead from '@/components/layout/SeoHead';
import QuoteSection from '@/components/sections/QuoteSection';
import { ArrowLeft, Check, ArrowRight, Download, Loader2 } from 'lucide-react';
import { POSTS, getPostBySlug, formatDate } from '@/blog/posts';
import { getBlogPostBySlug } from '@/lib/api';

const CANONICAL_ORIGIN = 'https://ohanapropertyexperts.com';

// Strict allowlist for content_html coming from external publishers (e.g. BabyLoveGrowth).
// Scripts, iframes, inline event handlers, and other unsafe elements are dropped.
const DOMPURIFY_CONFIG = {
  ALLOWED_TAGS: [
    'a', 'abbr', 'b', 'blockquote', 'br', 'caption', 'cite', 'code', 'col', 'colgroup',
    'dd', 'div', 'dl', 'dt', 'em', 'figcaption', 'figure', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'hr', 'i', 'img', 'kbd', 'li', 'mark', 'ol', 'p', 'pre', 'q', 's', 'samp', 'small',
    'span', 'strong', 'sub', 'sup', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'tr',
    'u', 'ul',
  ],
  ALLOWED_ATTR: ['href', 'title', 'alt', 'src', 'srcset', 'sizes', 'loading', 'rel', 'target', 'colspan', 'rowspan', 'scope'],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'textarea', 'select', 'button', 'style', 'link', 'meta'],
  FORBID_ATTR: ['style', 'onload', 'onclick', 'onerror', 'onfocus', 'onmouseover', 'onkeydown', 'onkeyup', 'onsubmit'],
  ALLOW_DATA_ATTR: false,
};

// On open in new tab, add rel attributes to keep links safe.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

function sanitize(html) {
  if (!html || typeof html !== 'string') return '';
  return DOMPurify.sanitize(html, DOMPURIFY_CONFIG);
}

// ---------- Block renderer for static (legacy) posts ----------

const LINK_RULES = [
  { pattern: /columbus\.in\.gov\/public-works/gi, href: () => 'https://www.columbus.in.gov/public-works/' },
  { pattern: /columbus\.in\.gov/gi, href: () => 'https://www.columbus.in.gov' },
  { pattern: /bcswmd\.com/gi, href: () => 'https://www.bcswmd.com' },
  { pattern: /https?:\/\/[^\s)]+/gi, href: (match) => match },
];

function linkify(text) {
  if (!text || typeof text !== 'string') return text;
  const combined = new RegExp(LINK_RULES.map((r) => `(${r.pattern.source})`).join('|'), 'gi');
  const parts = [];
  let lastIndex = 0;
  let m;
  while ((m = combined.exec(text)) !== null) {
    if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index));
    const matched = m[0];
    const rule = LINK_RULES.find((r) => new RegExp(`^(?:${r.pattern.source})$`, 'i').test(matched));
    const href = rule ? rule.href(matched) : matched;
    parts.push(
      <a
        key={`lnk-${m.index}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-ohana-primary font-semibold underline decoration-ohana-accent decoration-2 underline-offset-4 hover:text-ohana-accent-dark"
      >
        {matched}
      </a>,
    );
    lastIndex = m.index + matched.length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length ? parts : text;
}

function Block({ block }) {
  if (block.type === 'h2') {
    return <h2 className="font-serif text-3xl md:text-4xl text-ohana-primary leading-tight mt-12 mb-5">{block.text}</h2>;
  }
  if (block.type === 'h3') {
    return <h3 className="font-serif text-2xl text-ohana-primary leading-snug mt-10 mb-4">{block.text}</h3>;
  }
  if (block.type === 'list') {
    return (
      <ul className="my-6 space-y-3">
        {block.items.map((it, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="mt-1 grid h-5 w-5 flex-none place-items-center rounded-full bg-ohana-accent/15 text-ohana-accent">
              <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
            </span>
            <span className="text-ohana-text leading-relaxed">{linkify(it)}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === 'download_cta') {
    return (
      <aside
        className="my-10 rounded-2xl border border-ohana-accent/40 bg-ohana-accent/5 px-6 py-7 sm:px-8 sm:py-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5"
        data-testid="blog-download-cta"
      >
        <div className="flex-1 min-w-0">
          <p className="font-serif text-xl sm:text-2xl text-ohana-primary leading-tight">{block.title}</p>
          {block.body && <p className="mt-2 text-sm sm:text-base text-ohana-text leading-relaxed">{block.body}</p>}
        </div>
        <a
          href={block.href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-ohana-primary text-ohana-bg font-bold px-5 py-3 text-sm hover:bg-ohana-primary-dark transition-colors whitespace-nowrap"
          data-testid="blog-download-cta-button"
        >
          <Download className="h-4 w-4" />
          {block.label || 'Download'}
        </a>
      </aside>
    );
  }
  return <p className="text-ohana-text text-base md:text-lg leading-relaxed mb-5">{linkify(block.text)}</p>;
}

// ---------- Page ----------

export default function BlogPost() {
  const { slug } = useParams();
  const staticPost = getPostBySlug(slug);
  const [remotePost, setRemotePost] = useState(null);
  const [loading, setLoading] = useState(!staticPost);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (staticPost) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    getBlogPostBySlug(slug)
      .then((p) => {
        if (cancelled) return;
        setRemotePost(p);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err && err.status === 404) {
          setNotFound(true);
        } else if (process.env.NODE_ENV !== 'production') {
          console.warn('[BlogPost] failed to load post', err);
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, staticPost]);

  if (notFound) return <Navigate to="/blog" replace />;

  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-ohana-primary" />
      </div>
    );
  }

  if (staticPost) {
    const others = POSTS.filter((p) => p.slug !== staticPost.slug).slice(0, 3);
    const articleJsonLd = staticPost.author
      ? {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: staticPost.title,
          description: staticPost.excerpt,
          datePublished: staticPost.date,
          dateModified: staticPost.date,
          inLanguage: 'en-US',
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `${CANONICAL_ORIGIN}/blog/${staticPost.slug}`,
          },
          author: {
            '@type': 'Person',
            '@id': `${CANONICAL_ORIGIN}/#founder`,
            name: staticPost.author,
            jobTitle: 'Founder & Owner',
            worksFor: { '@id': `${CANONICAL_ORIGIN}/#organization` },
          },
          publisher: {
            '@type': 'Organization',
            '@id': `${CANONICAL_ORIGIN}/#organization`,
            name: 'Ohana Property & Transition Services LLC',
          },
        }
      : undefined;
    return (
      <>
        <SeoHead
          title={`${staticPost.title} | Ohana Property Services`}
          description={staticPost.excerpt}
          canonical={`${CANONICAL_ORIGIN}/blog/${staticPost.slug}`}
          ogType="article"
          jsonLd={articleJsonLd}
        />
        <article>
          <header className="bg-ohana-primary text-ohana-bg">
            <div className="container-ohana pt-24 pb-16 md:pt-32 md:pb-20 max-w-4xl">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-sm text-ohana-bg/70 hover:text-white transition mb-8"
                data-testid="blog-back-link"
              >
                <ArrowLeft className="h-4 w-4" />
                All Posts
              </Link>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center rounded-full bg-ohana-accent/20 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-ohana-accent">
                  {staticPost.category}
                </span>
                <span className="text-sm text-ohana-bg/60">{formatDate(staticPost.date)}</span>
                {staticPost.author && (
                  <span className="text-sm text-ohana-bg/60" data-testid="blog-post-author">
                    By {staticPost.author}
                  </span>
                )}
              </div>
              <h1 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.1]" data-testid="blog-post-title">
                {staticPost.title}
              </h1>
              <p className="mt-6 text-lg md:text-xl text-ohana-bg/80 leading-relaxed">{staticPost.excerpt}</p>
            </div>
          </header>
          <div className="bg-ohana-bg">
            <div className="container-ohana py-16 md:py-24">
              <div className="mx-auto max-w-3xl" data-testid="blog-post-body">
                {staticPost.body.map((block, idx) => <Block key={idx} block={block} />)}
              </div>
            </div>
          </div>
        </article>
        {others.length > 0 && (
          <section className="section-pad bg-ohana-surface" data-testid="blog-related">
            <div className="container-ohana">
              <p className="overline">Keep Reading</p>
              <h2 className="h-display text-3xl md:text-4xl mt-3 leading-tight">More from the crew.</h2>
              <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
                {others.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/blog/${p.slug}`}
                    className="group rounded-2xl bg-white border border-ohana-border p-6 transition hover:-translate-y-1 hover:shadow-xl hover:shadow-ohana-primary/5"
                    data-testid={`blog-related-${p.slug}`}
                  >
                    <span className="inline-flex items-center rounded-full bg-ohana-surface px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-ohana-primary">
                      {p.category}
                    </span>
                    <h3 className="mt-4 font-serif text-xl text-ohana-primary leading-snug">{p.title}</h3>
                    <span className="mt-4 inline-flex items-center text-sm font-bold text-ohana-accent group-hover:translate-x-1 transition-transform">
                      Read <ArrowRight className="ml-1 h-4 w-4" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
        <QuoteSection />
      </>
    );
  }

  if (!remotePost) return <Navigate to="/blog" replace />;

  const cleanHtml = sanitize(remotePost.content_html);
  const canonical = remotePost.public_url || `${CANONICAL_ORIGIN}/blog/${remotePost.slug}`;
  const dateLabel = (remotePost.published_at || '').slice(0, 10);

  return (
    <>
      <SeoHead
        title={`${remotePost.title} | Ohana Property Services`}
        description={remotePost.meta_description}
        canonical={canonical}
        ogType="article"
        ogImage={remotePost.hero_image_url}
        jsonLd={remotePost.json_ld}
        faqJsonLd={remotePost.faq_json_ld}
      />
      <article data-testid={`blog-post-${remotePost.slug}`}>
        <header className="bg-ohana-primary text-ohana-bg">
          <div className="container-ohana pt-24 pb-16 md:pt-32 md:pb-20 max-w-4xl">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm text-ohana-bg/70 hover:text-white transition mb-8"
              data-testid="blog-back-link"
            >
              <ArrowLeft className="h-4 w-4" />
              All Posts
            </Link>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center rounded-full bg-ohana-accent/20 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-ohana-accent">
                Resource
              </span>
              {dateLabel && <span className="text-sm text-ohana-bg/60">{formatDate(dateLabel)}</span>}
            </div>
            <h1
              className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.1]"
              data-testid="blog-post-title"
            >
              {remotePost.title}
            </h1>
            {remotePost.meta_description && (
              <p className="mt-6 text-lg md:text-xl text-ohana-bg/80 leading-relaxed">
                {remotePost.meta_description}
              </p>
            )}
          </div>
        </header>

        {remotePost.hero_image_url && (
          <div className="bg-ohana-bg">
            <div className="container-ohana max-w-4xl pt-12 md:pt-16">
              <img
                src={remotePost.hero_image_url}
                alt={remotePost.title}
                loading="lazy"
                className="w-full h-auto rounded-2xl border border-ohana-border"
                data-testid="blog-post-hero-image"
              />
            </div>
          </div>
        )}

        <div className="bg-ohana-bg">
          <div className="container-ohana py-16 md:py-24">
            <div
              className="mx-auto max-w-3xl blog-html"
              data-testid="blog-post-body"
              dangerouslySetInnerHTML={{ __html: cleanHtml }}
            />
          </div>
        </div>
      </article>

      <QuoteSection />
    </>
  );
}
